import {eq} from './isEqual';
/**
 * Performs a deep equality check between two object values selectively.
 *
 * @example
 * const source = { a: 1, b:1 };
 * const target = { a: 1 };
 *
 * isObjectMatches(foo, bar);
 * // => true
 * const source = { a: 1, b:1 };
 * const target = { a: 2 };
 *
 * foo === bar
 * // => false
 *
 * @function isObjectMatches
 * @param {*} source The value to compare.
 * @param {*} target The value to compare `source` against selectively.
 * @return {boolean} Returns `true` if the target values are selectively equal, otherwise `false`.
 */

function isObjectMatches(source, target, options = {}) {
	if (!source || !target) return false;
	if (typeof target !== 'object' && typeof source !== 'object') return false;
	for (var key in target) {
		if (target.hasOwnProperty(key) && !source.hasOwnProperty(key)) return false;
		if (source.hasOwnProperty(key) && !isSelectiveEqual(source[key], target[key], options))
			return false;
	}
	return true;
}

/**
 * Performs a deep equality check between two values of array.
 *
 * @example
 * const source = [{ a: 1 }, { b:1 }];
 * const target = [{ a: 1 }, { b:1 }];
 *
 * isArrayMatches(foo, bar);
 * // => true
 *
 * foo === bar
 * // => false
 *
 * @function isArrayMatches
 * @param {*} source The value to compare.
 * @param {*} target The value to compare `source` against selectively.
 * @return {boolean} Returns `true` if the target values are selectively equal, otherwise `false`.
 */

function isArrayMatches(source, target, options) {
	const {length: sourceLength} = source;
	const {length: targetLength} = target;

	if (sourceLength !== targetLength) return false;

	for (let i = 0; i < sourceLength; i++) {
		if (!isSelectiveEqual(source[i], target[i], options)) return false;
	}

	return true;
}

/**
 * Performs a deep equality check between two values selectively.
 *
 * @example
 * const source = { a: 1, b:1 };
 * const target = { a: 1 };
 *
 * isSelectiveEqual(foo, bar);
 * // => true
 *
 * foo === bar
 * // => false
 *
 * @function isSelectiveEqual
 * @param {*} source The value to compare.
 * @param {*} target The value to compare `source` against selectively.
 * @return {boolean} Returns `true` if the target values are selectively equal, otherwise `false`.
 */

export default function isSelectiveEqual(source, target, options = {}) {
	if (source === target) return true;
	if (Array.isArray(source) && Array.isArray(target))
		return isArrayMatches(source, target, options);
	const result = isObjectTagMatches(source, target, options);
	if (result !== undefined) return result;

	if (typeof source === 'object' && typeof target === 'object')
		return isObjectMatches(source, target, options);

	return typeof source === 'function' && typeof target === 'function' && source === target;
}

/**
 * Performs a object type comparision based on different data types of object and delegates the comparision to respective functions
 *
 * @example
 * const source = { a: 1, b:1 };
 * const target = { a: 1 };
 *
 * isObjectTagMatches(foo, bar);
 * // => true
 *
 * const source = { a: 1, b:1 };
 * const target = { a: 2 };
 * // => false
 *
 * @function isObjectTagMatches
 * @param {*} source The value to compare.
 * @param {*} target The value to compare `source` against selectively.
 * @return {boolean} Returns `true` if the target values are selectively equal, otherwise `false`.
 */

function isObjectTagMatches(source, target, options) {
	const {classes = true} = options;
	const sourceTag = Object.prototype.toString.call(source);
	const targetTag = Object.prototype.toString.call(target);

	if (sourceTag !== targetTag) return false;

	switch (sourceTag) {
		case '[object Boolean]':
		case '[object Number]':
		case '[object Date]':
			return eq(+source, +target);

		case '[object Set]':
			return isArrayMatches([...source], [...target], options);

		case '[object Map]':
			return isObjectMatches(Object.fromEntries(source), Object.fromEntries(target), options);

		case '[object String]':
			return `${source}` === `${target}`;

		case '[object Object]':
			if (!classes && source.constructor !== Object) return source === target;
			return isObjectMatches(source, target, options);
	}
}
